export interface AuthUser {
  id: string;
  telegramUserId: string;
  username: string | null;
  firstName: string | null;
  lastName: string | null;
  photoUrl: string | null;
  languageCode: string | null;
  role: 'USER' | 'ADMIN';
  status: 'ACTIVE' | 'BLOCKED';
}

export interface AuthSession {
  user: AuthUser;
  csrfToken: string | null;
}

export interface AnalysisRequest {
  id: string;
  status: string;
  youtubeVideoId: string;
  canonicalUrl: string;
}

export interface AnalysisVideo {
  youtubeVideoId: string;
  canonicalUrl: string;
  title: string | null;
  channelName: string | null;
  thumbnailUrl: string | null;
}

export interface AnalysisRequester {
  firstName: string | null;
  username: string | null;
}

export interface AnalysisListItem {
  id: string;
  status: string;
  createdAt: string;
  video: AnalysisVideo;
  requestedBy?: AnalysisRequester | null;
  provider?: string | null;
}

export interface AnalysisSummary {
  overview?: string;
  /** Краткий пересказ тезисами — появился вместе с разбором по главам. */
  keyPoints?: string[];
  keyTopics?: string[];
  language?: string;
}

/** Глава подробного пересказа: таймкод, заголовок и тезисы фрагмента ролика. */
export interface DigestChapter {
  order: number;
  startMs: number;
  title: string;
  bullets: string[];
  highlight: boolean;
}

/** Готовый разбор, отданный по секретной ссылке — без данных о том, кто его заказывал. */
export interface PublicReport {
  id: string;
  createdAt: string;
  video: AnalysisVideo & {
    durationSeconds: number | null;
    publishedAt: string | null;
  };
  summary: AnalysisSummary | null;
  chapters: DigestChapter[];
  claims: AnalysisClaim[];
}

export interface AnalysisClaim {
  order: number;
  text: string;
  context: string | null;
  category: string | null;
  importance: number;
  riskLevel: number;
}

export interface AnalysisStepUsage {
  provider?: string;
  model?: string;
}

export interface AnalysisStep {
  stage: string;
  status: string;
  errorMessage: string | null;
  output?: { usage?: AnalysisStepUsage } | null;
}

export interface AnalysisDetail {
  id: string;
  status: string;
  createdAt: string;
  errorMessage: string | null;
  summary: AnalysisSummary | null;
  video: AnalysisVideo & {
    durationSeconds: number | null;
    detectedLanguage: string | null;
    publishedAt: string | null;
  };
  steps: AnalysisStep[];
  claims: AnalysisClaim[];
}

/** Глобальные настройки слежения; chat id приходит строкой — BigInt в JSON не влезает. */
export interface WatchSettings {
  checkIntervalMinutes: number;
  defaultDeliveryChatId: string | null;
  defaultMinDurationSeconds: number;
  defaultMaxDurationSeconds: number | null;
  sttEnabled: boolean;
}

export interface WatchedChannelView {
  id: string;
  youtubeChannelId: string;
  title: string | null;
  url: string;
  enabled: boolean;
  deliveryChatId: string | null;
  minDurationSeconds: number | null;
  maxDurationSeconds: number | null;
  lastPublishedAt: string | null;
  lastCheckedAt: string | null;
  lastError: string | null;
}

export interface AdminSettingsPayload {
  settings: WatchSettings;
  channels: WatchedChannelView[];
}

export interface AuthApi {
  restore(): Promise<AuthSession | null>;
  login(initData: string): Promise<AuthSession>;
  loginWithPassword(login: string, password: string): Promise<AuthSession>;
  logout(): Promise<void>;
  createAnalysis(url: string): Promise<AnalysisRequest>;
  listAnalyses(): Promise<AnalysisListItem[]>;
  getAnalysis(id: string): Promise<AnalysisDetail>;
  getPublicReport(token: string): Promise<PublicReport>;
  getAdminSettings(): Promise<AdminSettingsPayload>;
  saveAdminSettings(patch: Partial<WatchSettings>): Promise<WatchSettings>;
  addChannel(url: string): Promise<WatchedChannelView>;
  updateChannel(id: string, patch: Partial<WatchedChannelView>): Promise<WatchedChannelView>;
  removeChannel(id: string): Promise<void>;
  checkChannelsNow(): Promise<void>;
}

interface AuthApiOptions {
  fetcher?: typeof fetch;
  readCookies?: () => string;
}

interface ErrorPayload {
  message?: unknown;
}

const CSRF_COOKIE_NAME = 'stormview_csrf';

export function readCookie(name: string, cookieHeader: string): string | null {
  for (const part of cookieHeader.split(';')) {
    const separatorIndex = part.indexOf('=');
    if (separatorIndex < 0) continue;

    const key = part.slice(0, separatorIndex).trim();
    if (key !== name) continue;

    const encodedValue = part.slice(separatorIndex + 1).trim();
    try {
      return decodeURIComponent(encodedValue);
    } catch {
      return null;
    }
  }

  return null;
}

async function errorFromResponse(response: Response): Promise<Error> {
  let message = `Ошибка запроса (${response.status})`;

  try {
    const payload = (await response.json()) as ErrorPayload;
    if (typeof payload.message === 'string' && payload.message.length > 0) {
      message = payload.message;
    }
  } catch {
    // The status-based fallback remains useful for non-JSON proxy errors.
  }

  return new Error(message);
}

export function createAuthApi(options: AuthApiOptions = {}): AuthApi {
  const fetcher = options.fetcher ?? fetch;
  const readCookies = options.readCookies ?? (() => document.cookie);

  /** Изменяющий запрос: те же куки и CSRF-заголовок, что у остальных мутаций приложения. */
  async function mutate(path: string, method: string, body?: unknown): Promise<unknown> {
    const csrfToken = readCookie(CSRF_COOKIE_NAME, readCookies());
    if (!csrfToken) {
      throw new Error('CSRF-токен недоступен');
    }

    const headers: Record<string, string> = {
      accept: 'application/json',
      'x-csrf-token': csrfToken,
    };
    if (body !== undefined) headers['content-type'] = 'application/json';

    const response = await fetcher(path, {
      method,
      credentials: 'include',
      headers,
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });

    if (!response.ok) throw await errorFromResponse(response);
    // 204 без тела — например, удаление канала.
    return response.status === 204 ? null : await response.json();
  }

  return {
    async restore(): Promise<AuthSession | null> {
      const response = await fetcher('/auth/me', {
        credentials: 'include',
        headers: { accept: 'application/json' },
      });

      if (response.status === 401) return null;
      if (!response.ok) throw await errorFromResponse(response);

      const payload = (await response.json()) as { user: AuthUser };
      return {
        user: payload.user,
        csrfToken: readCookie(CSRF_COOKIE_NAME, readCookies()),
      };
    },

    async login(initData: string): Promise<AuthSession> {
      const response = await fetcher('/auth/telegram/mini-app', {
        method: 'POST',
        credentials: 'include',
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
        },
        body: JSON.stringify({ initData }),
      });

      if (!response.ok) throw await errorFromResponse(response);

      const payload = (await response.json()) as {
        user: AuthUser;
        csrfToken: string;
      };
      return {
        user: payload.user,
        csrfToken: payload.csrfToken,
      };
    },

    async loginWithPassword(login: string, password: string): Promise<AuthSession> {
      const response = await fetcher('/auth/login', {
        method: 'POST',
        credentials: 'include',
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
        },
        body: JSON.stringify({ login, password }),
      });

      if (!response.ok) throw await errorFromResponse(response);

      const payload = (await response.json()) as {
        user: AuthUser;
        csrfToken: string;
      };
      return {
        user: payload.user,
        csrfToken: payload.csrfToken,
      };
    },

    async logout(): Promise<void> {
      const csrfToken = readCookie(CSRF_COOKIE_NAME, readCookies());
      if (!csrfToken) {
        throw new Error('CSRF-токен недоступен');
      }

      const response = await fetcher('/auth/logout', {
        method: 'POST',
        credentials: 'include',
        headers: {
          accept: 'application/json',
          'x-csrf-token': csrfToken,
        },
      });

      if (!response.ok) throw await errorFromResponse(response);
    },

    async createAnalysis(url: string): Promise<AnalysisRequest> {
      const csrfToken = readCookie(CSRF_COOKIE_NAME, readCookies());
      if (!csrfToken) {
        throw new Error('CSRF-токен недоступен');
      }

      const response = await fetcher('/analyses', {
        method: 'POST',
        credentials: 'include',
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
          'x-csrf-token': csrfToken,
        },
        body: JSON.stringify({ url }),
      });

      if (!response.ok) throw await errorFromResponse(response);

      return (await response.json()) as AnalysisRequest;
    },

    async listAnalyses(): Promise<AnalysisListItem[]> {
      const response = await fetcher('/analyses', {
        credentials: 'include',
        headers: { accept: 'application/json' },
      });

      if (!response.ok) throw await errorFromResponse(response);

      const payload = (await response.json()) as { analyses: AnalysisListItem[] };
      return payload.analyses;
    },

    async getAdminSettings(): Promise<AdminSettingsPayload> {
      const response = await fetcher('/admin/settings', {
        credentials: 'include',
        headers: { accept: 'application/json' },
      });
      if (!response.ok) throw await errorFromResponse(response);
      return (await response.json()) as AdminSettingsPayload;
    },

    async saveAdminSettings(patch: Partial<WatchSettings>): Promise<WatchSettings> {
      const payload = await mutate('/admin/settings', 'PUT', patch);
      return (payload as { settings: WatchSettings }).settings;
    },

    async addChannel(url: string): Promise<WatchedChannelView> {
      const payload = await mutate('/admin/channels', 'POST', { url });
      return (payload as { channel: WatchedChannelView }).channel;
    },

    async updateChannel(
      id: string,
      patch: Partial<WatchedChannelView>,
    ): Promise<WatchedChannelView> {
      const payload = await mutate(`/admin/channels/${encodeURIComponent(id)}`, 'PATCH', patch);
      return (payload as { channel: WatchedChannelView }).channel;
    },

    async removeChannel(id: string): Promise<void> {
      await mutate(`/admin/channels/${encodeURIComponent(id)}`, 'DELETE');
    },

    async checkChannelsNow(): Promise<void> {
      await mutate('/admin/channels/check', 'POST');
    },

    /** Публичная карточка: без cookie — её и не должно быть у случайного читателя. */
    async getPublicReport(token: string): Promise<PublicReport> {
      const response = await fetcher(`/public/analyses/${encodeURIComponent(token)}`, {
        headers: { accept: 'application/json' },
      });

      if (!response.ok) throw await errorFromResponse(response);

      return (await response.json()) as PublicReport;
    },

    async getAnalysis(id: string): Promise<AnalysisDetail> {
      const response = await fetcher(`/analyses/${encodeURIComponent(id)}`, {
        credentials: 'include',
        headers: { accept: 'application/json' },
      });

      if (!response.ok) throw await errorFromResponse(response);

      return (await response.json()) as AnalysisDetail;
    },
  };
}
